import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChefHat, Crown, Sparkles, Fingerprint, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { 
  isPlatformAuthenticatorAvailable, authenticateCredential, 
  getStoredEmail, clearStoredEmail 
} from '../lib/webauthn';
import axios from 'axios';

const API = '/api';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

export default function Login() {
  const navigate = useNavigate();
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricEmail, setBiometricEmail] = useState(null);
  const [biometricLoading, setBiometricLoading] = useState(false);

  useEffect(() => {
    checkBiometricAvailability();
  }, []);

  const checkBiometricAvailability = async () => {
    const supported = await isPlatformAuthenticatorAvailable();
    const email = getStoredEmail();
    if (supported && email) {
      setBiometricAvailable(true);
      setBiometricEmail(email);
    }
  };

  const handleBiometricLogin = async () => {
    setBiometricLoading(true);
    try {
      // Get authentication options
      const optionsRes = await axios.post(`${API}/auth/webauthn/login/options`, { email: biometricEmail });
      const options = optionsRes.data;
      
      // Authenticate with Face ID / Touch ID
      const credential = await authenticateCredential(options);
      
      // Verify with backend
      const verifyRes = await axios.post(`${API}/auth/webauthn/login/verify`, {
        email: biometricEmail,
        ...credential
      });
      
      // Store session and redirect
      if (verifyRes.data.token) {
        localStorage.setItem('session_token', verifyRes.data.token);
      }
      window.location.href = '/dashboard';
    } catch (err) {
      console.error('Biometric login error:', err);
      // If biometric fails, clear stored email and show Google login
      if (err.name === 'NotAllowedError') {
        // User cancelled
      } else {
        clearStoredEmail();
        setBiometricAvailable(false);
      }
    } finally {
      setBiometricLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Redirect to backend-served callback page (not React) to avoid service worker/caching/framework issues
    const redirectUrl = window.location.origin + '/api/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left side - Branding */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1635500740083-2a916bd01600?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwyfHxmaW5lJTIwZGluaW5nJTIwcmVzdGF1cmFudCUyMGludGVyaW9yJTIwYmx1cnxlbnwwfHx8fDE3NzI2OTc0NTF8MA&ixlib=rb-4.1.0&q=85')`
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/50 to-amber-900/30"></div>
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-amber-600 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/30">
                <ChefHat className="w-6 h-6 text-white" />
              </div>
              <div>
                <span className="text-2xl font-bold tracking-tight">ROCH</span>
                <p className="text-amber-400 text-xs font-medium tracking-widest">HOSPITALITY</p>
              </div>
            </div>
          </div>
          
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Crown className="w-5 h-5 text-amber-400" />
              <span className="text-amber-400 text-sm font-medium">Founder Command Center</span>
            </div>
            <blockquote className="text-2xl font-light leading-relaxed mb-4">
              "Financial clarity across all ventures, in under 10 minutes a day."
            </blockquote>
            <p className="text-white/60 text-sm">— Built for hospitality founders</p>
          </div>
        </div>
      </div>

      {/* Right side - Login */}
      <div className="flex-1 flex items-center justify-center p-8 bg-gradient-to-br from-white via-white to-amber-50/30">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-11 h-11 bg-gradient-to-br from-amber-400 to-amber-600 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/20">
              <ChefHat className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="text-xl font-bold text-zinc-900">ROCH</span>
              <p className="text-amber-600 text-[10px] font-medium tracking-widest">HOSPITALITY</p>
            </div>
          </div>

          <div className="mb-8">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-5 h-5 text-amber-500" />
              <span className="text-xs font-medium text-amber-600 tracking-wide uppercase">Premium Access</span>
            </div>
            <h1 className="text-3xl font-semibold tracking-tight text-zinc-900 mb-2">
              Welcome back
            </h1>
            <p className="text-zinc-500">
              Sign in to access your financial command center
            </p>
          </div>

          {/* Face ID / Touch ID Quick Login */}
          {biometricAvailable && (
            <div className="mb-6">
              <Button
                onClick={handleBiometricLogin}
                disabled={biometricLoading}
                className="w-full h-14 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-medium flex items-center justify-center gap-3 shadow-lg shadow-emerald-600/20 transition-all hover:shadow-xl mb-3"
                data-testid="biometric-login-btn"
              >
                {biometricLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Fingerprint className="w-5 h-5" />
                )}
                {biometricLoading ? 'Authenticating...' : 'Sign in with Face ID'}
              </Button>
              <p className="text-center text-xs text-zinc-400">
                Quick login as {biometricEmail}
              </p>
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-zinc-200"></div>
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="px-2 bg-white text-zinc-400">or</span>
                </div>
              </div>
            </div>
          )}

          <Button
            onClick={handleGoogleLogin}
            className="w-full h-14 rounded-xl bg-gradient-to-r from-zinc-900 to-zinc-800 hover:from-zinc-800 hover:to-zinc-700 text-white font-medium flex items-center justify-center gap-3 shadow-lg shadow-zinc-900/20 transition-all hover:shadow-xl hover:shadow-zinc-900/30"
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Continue with Google
          </Button>

          <p className="text-center text-xs text-zinc-400 mt-8">
            By signing in, you agree to our Terms of Service and Privacy Policy
          </p>

          <div className="mt-12 pt-8 border-t border-amber-100">
            <h3 className="text-xs font-medium uppercase tracking-wider text-amber-600 mb-4 flex items-center gap-2">
              <Crown className="w-3.5 h-3.5" />
              Trusted by hospitality ventures
            </h3>
            <div className="flex flex-wrap gap-3">
              <span className="px-3 py-1.5 bg-amber-50 text-amber-700 rounded-full text-sm font-medium border border-amber-100">ROCH Cake Studio</span>
              <span className="px-3 py-1.5 bg-amber-50 text-amber-700 rounded-full text-sm font-medium border border-amber-100">Chef's Table</span>
              <span className="px-3 py-1.5 bg-amber-50 text-amber-700 rounded-full text-sm font-medium border border-amber-100">Hospitality Consulting</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
